import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Check,
  History,
  PhoneCall,
  Plus,
  RefreshCw,
  RotateCcw,
  Send,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { seguimientoApi } from "./api";
import { invitationsApi } from "../onboarding-invitations/api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { ApplicationsTable, type Column } from "../applications/shared/ApplicationsTable";
import { formatDate } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { Avatar, Badge, FieldLabel } from "./ui";
import {
  ACTIVITY_KIND_LABELS,
  ACTIVITY_KIND_TONE,
  ALL_NEXT_ACTIONS,
  LEAD_SOURCES,
  NEXT_ACTIONS_BY_STAGE,
  STAGE_LABELS,
  STAGE_ORDER,
  STAGE_TONE,
  TYPE_LABELS,
  type Tone,
} from "./labels";
import type { Prospect, ProspectActivity, ProspectStage, ProspectType, TeamMember } from "./types";

/** Mapeo perfil CRM (es) → onboarding (en) para invitar desde el prospecto. */
const PROFILE_EN: Record<ProspectType, "artist" | "producer" | "provider"> = {
  artista: "artist",
  productor: "producer",
  proveedor: "provider",
};

const INV_STATUS_META: Record<string, { label: string; tone: Tone }> = {
  invited: { label: "Enviada", tone: "info" },
  accepted: { label: "Aceptada", tone: "success" },
  expired: { label: "Vencida", tone: "neutral" },
  revoked: { label: "Anulada", tone: "danger" },
};

const TYPE_FILTERS = [
  { value: "", label: "Todos" },
  { value: "artista", label: "Artistas" },
  { value: "productor", label: "Productores" },
  { value: "proveedor", label: "Proveedores" },
];

function toDateInput(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : "";
}

export function FunnelPane() {
  // La campana / deep-links pueden abrir el Funnel ya filtrado (?alert=true) o
  // enfocado en un prospecto (?focus=prospect:<id>).
  const [searchParams, setSearchParams] = useSearchParams();
  const [type, setType] = useState("");
  const [responsable, setResponsable] = useState("");
  const [alertOnly, setAlertOnly] = useState(searchParams.get("alert") === "true");
  // Filtro por etapa (client-side, multi-select): vacío = todas. Permite
  // "ver solo correo enviado" o "ocultar perfil completo" sin tocar la API.
  const [stageFilter, setStageFilter] = useState<Set<ProspectStage>>(new Set());
  const [editing, setEditing] = useState<Prospect | null | "new">(null);
  // Deep-link desde Reclutadores: ?new=productor abre "Nuevo prospecto" con ese tipo.
  const [newType, setNewType] = useState<ProspectType | undefined>(undefined);

  const toggleStage = (s: ProspectStage) =>
    setStageFilter((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });

  const teamQuery = useQuery({ queryKey: ["seg", "team"], queryFn: seguimientoApi.team });

  const listQuery = useQuery({
    queryKey: ["seg", "funnel", { type, responsable, alertOnly }],
    queryFn: () =>
      seguimientoApi.funnel({
        ...(type ? { type } : {}),
        ...(responsable ? { responsableUserId: responsable } : {}),
        ...(alertOnly ? { alertOnly: "true" } : {}),
      }),
  });

  const allRows = listQuery.data ?? [];
  const rows = stageFilter.size
    ? allRows.filter((r) => stageFilter.has(r.stage))
    : allRows;

  // Foco por deep-link: abrir el drawer del prospecto indicado una vez que está
  // en la lista, y limpiar el param para no re-abrir. Busca en TODAS las filas
  // (ignora el filtro de etapa, para que el deep-link siempre abra).
  const newParam = searchParams.get("new");
  useEffect(() => {
    if (!newParam) return;
    if (newParam === "productor" || newParam === "artista" || newParam === "proveedor") {
      setNewType(newParam);
      setEditing("new");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newParam]);

  const focus = searchParams.get("focus");
  useEffect(() => {
    if (!focus || !focus.startsWith("prospect:")) return;
    const id = Number(focus.split(":")[1]);
    if (!id) return;
    const target = allRows.find((r) => r.id === id);
    if (target) {
      setEditing(target);
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, allRows]);

  const columns: Column<Prospect>[] = [
    {
      key: "name",
      header: "Prospecto",
      cell: (r) => (
        <div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-fg">{r.name}</span>
            {r.linkedUser && (
              <Badge tone="success">
                <Check size={11} /> {r.linkedUser.isProducer || r.linkedUser.isArtist || r.linkedUser.isPartner ? "activo" : "registrado"}
              </Badge>
            )}
          </div>
          <div className="text-2xs text-fg-subtle break-all">{r.contactEmail ?? r.phone ?? "—"}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Tipo",
      cell: (r) => <span className="text-xs text-fg-muted">{TYPE_LABELS[r.type]}</span>,
      width: "min",
    },
    {
      key: "stage",
      header: "Etapa",
      cell: (r) => <Badge tone={STAGE_TONE[r.stage]}>{STAGE_LABELS[r.stage]}</Badge>,
      width: "min",
    },
    {
      key: "invitation",
      header: "Invitación",
      cell: (r) => <InvitationCell prospect={r} />,
      width: "min",
    },
    {
      key: "responsable",
      header: "Resp.",
      cell: (r) =>
        r.responsable ? (
          <span className="inline-flex items-center gap-1.5">
            <Avatar name={r.responsable.name} size={20} />
            <span className="text-xs text-fg">{r.responsable.name.split(" ")[0]}</span>
          </span>
        ) : (
          <span className="text-2xs text-fg-subtle">—</span>
        ),
      width: "min",
    },
    {
      key: "nextAction",
      header: "Próxima acción",
      cell: (r) => (
        <span className="text-xs text-fg-muted line-clamp-1 max-w-[260px]">
          {r.nextAction ?? "—"}
        </span>
      ),
    },
    {
      key: "lastContact",
      header: "Últ. contacto",
      cell: (r) =>
        r.alert ? (
          <Badge tone="danger">⚠ {r.daysSinceContact} días</Badge>
        ) : (
          <span className="text-xs text-fg-subtle">{formatDate(r.lastContactAt)}</span>
        ),
      width: "min",
      align: "right",
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <select value={type} onChange={(e) => setType(e.target.value)} className="nx-input w-auto">
          {TYPE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={responsable}
          onChange={(e) => setResponsable(e.target.value)}
          className="nx-input w-auto"
        >
          <option value="">Todos los responsables</option>
          {(teamQuery.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-fg-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={alertOnly}
            onChange={(e) => setAlertOnly(e.target.checked)}
          />
          Solo ⚠ por seguir
        </label>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary ml-auto"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
        <button onClick={() => setEditing("new")} className="nx-btn-primary">
          <Plus size={14} /> Nuevo prospecto
        </button>
      </div>

      {/* Filtro por etapa (multi-select). Vacío = todas. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-2xs text-fg-subtle mr-1">Etapa:</span>
        {STAGE_ORDER.map((s) => {
          const on = stageFilter.has(s);
          return (
            <button
              key={s}
              onClick={() => toggleStage(s)}
              className={`px-2 py-0.5 rounded-full text-2xs border transition-colors ${
                on
                  ? "border-accent bg-accent-soft text-accent font-medium"
                  : "border-border text-fg-muted hover:bg-bg-subtle"
              }`}
            >
              {STAGE_LABELS[s]}
            </button>
          );
        })}
        {stageFilter.size > 0 && (
          <button
            onClick={() => setStageFilter(new Set())}
            className="text-2xs text-fg-subtle underline ml-1"
          >
            limpiar
          </button>
        )}
      </div>

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando funnel…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<Prospect>
          rows={rows}
          columns={columns}
          page={1}
          pageSize={rows.length || 1}
          total={rows.length}
          onPageChange={() => {}}
          onRowClick={(r) => setEditing(r)}
          selectedId={editing && editing !== "new" ? editing.id : null}
          emptyLabel="No hay prospectos con ese filtro."
        />
      )}

      {editing !== null && (
        <ProspectDrawer
          prospect={editing === "new" ? null : editing}
          defaultType={editing === "new" ? newType : undefined}
          team={teamQuery.data ?? []}
          onClose={() => {
            setEditing(null);
            setNewType(undefined);
          }}
        />
      )}
    </div>
  );
}

/**
 * Estado de la invitación de onboarding embebido en la fila del prospecto +
 * acción inline. Lee el snapshot (sin fetch extra). Invitar pre-llena
 * email/tipo del prospecto y linkea (crmProspectId).
 */
function InvitationCell({ prospect }: { prospect: Prospect }) {
  const qc = useQueryClient();
  const { push } = useToast();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["seg", "funnel"] });
    qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
    qc.invalidateQueries({ queryKey: ["seg", "activities", prospect.id] });
    qc.invalidateQueries({ queryKey: ["onboarding-invitations"] });
  };

  const create = useMutation({
    mutationFn: () =>
      invitationsApi.create(
        PROFILE_EN[prospect.type],
        String(prospect.contactEmail || "").trim().toLowerCase(),
        prospect.id,
      ),
    onSuccess: () => {
      push({ kind: "success", message: "Invitación enviada." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo invitar." }),
  });

  // La ↻ de la fila reenvía un RECORDATORIO (copy "recuerda activar…" según
  // etapa/perfil), no la invitación de bienvenida original.
  const resend = useMutation({
    mutationFn: () => invitationsApi.resend(prospect.lastInvitationId as number, true),
    onSuccess: () => {
      push({ kind: "success", message: "Recordatorio enviado." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo reenviar." }),
  });

  const status = prospect.lastInvitationStatus;
  const opened = !!prospect.lastInvitationOpenedAt;
  const hasEmail = !!String(prospect.contactEmail || "").trim();
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  if (status === "accepted") {
    return <Badge tone="success">Aceptada</Badge>;
  }
  if (status === "invited") {
    return (
      <span className="inline-flex items-center gap-1.5" onClick={stop}>
        <Badge tone={opened ? "accent" : "info"}>{opened ? "Abrió" : "Enviada"}</Badge>
        <button
          className="nx-btn-ghost p-1"
          disabled={resend.isPending}
          onClick={() => resend.mutate()}
          title="Reenviar recordatorio"
          aria-label="Reenviar recordatorio"
        >
          <RotateCcw size={12} />
        </button>
      </span>
    );
  }
  // Sin invitación activa (none / expired / revoked) → invitar.
  return (
    <span className="inline-flex items-center gap-1.5" onClick={stop}>
      {(status === "expired" || status === "revoked") && (
        <Badge tone={INV_STATUS_META[status].tone}>{INV_STATUS_META[status].label}</Badge>
      )}
      <button
        className="nx-btn-secondary px-2 py-1 text-2xs"
        disabled={!hasEmail || create.isPending}
        onClick={() => create.mutate()}
        title={hasEmail ? "Enviar invitación de onboarding" : "Cargá un email en el prospecto primero"}
      >
        <Send size={12} /> {create.isPending ? "…" : "Invitar"}
      </button>
    </span>
  );
}

type Form = {
  type: ProspectType;
  name: string;
  phone: string;
  contactEmail: string;
  source: string;
  stage: ProspectStage;
  responsableUserId: string;
  nextAction: string;
  lastContactAt: string;
  remindAt: string;
  notes: string;
  archived: boolean;
};

/**
 * Select con opciones fijas + escape a texto libre ("Otra…"). Lo usan
 * "Próxima acción" (opciones agrupadas por etapa) y "Origen". Si el valor no
 * está en ninguna opción (texto legacy / custom), arranca en modo libre.
 */
function SelectOrCustom({
  value,
  onChange,
  groups,
  placeholder,
  emptyLabel = "— Sin definir —",
}: {
  value: string;
  onChange: (v: string) => void;
  groups: { label?: string; options: string[] }[];
  placeholder?: string;
  emptyLabel?: string;
}) {
  const all = groups.flatMap((g) => g.options);
  const [custom, setCustom] = useState(!!value && !all.includes(value));
  // Distingue un cambio TIPEADO (el usuario escribe en modo libre) de uno
  // PROGRAMÁTICO (autofill por etapa). Solo en el segundo caso salimos del
  // modo libre — si no, tipear un texto que coincide con una opción conocida
  // (ej "Otro", "Reenviar recordatorio") cierra el input a mitad de escribir.
  const fromInput = useRef(false);

  useEffect(() => {
    if (fromInput.current) {
      fromInput.current = false;
      return;
    }
    if (value && all.includes(value)) setCustom(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <>
      <select
        className="nx-input"
        value={custom ? "__custom__" : value}
        onChange={(e) => {
          if (e.target.value === "__custom__") {
            setCustom(true);
          } else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="">{emptyLabel}</option>
        {groups.map((g, i) =>
          g.label ? (
            <optgroup key={i} label={g.label}>
              {g.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </optgroup>
          ) : (
            g.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))
          ),
        )}
        <option value="__custom__">Otra (escribir)…</option>
      </select>
      {custom && (
        <input
          className="nx-input mt-1.5"
          value={value}
          placeholder={placeholder ?? "Escribir…"}
          onChange={(e) => {
            fromInput.current = true;
            onChange(e.target.value);
          }}
          autoFocus
        />
      )}
    </>
  );
}

function ProspectDrawer({
  prospect,
  defaultType,
  team,
  onClose,
}: {
  prospect: Prospect | null;
  defaultType?: ProspectType;
  team: TeamMember[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();
  const isNew = prospect === null;

  const [form, setForm] = useState<Form>({
    type: prospect?.type ?? defaultType ?? "artista",
    name: prospect?.name ?? "",
    phone: prospect?.phone ?? "",
    contactEmail: prospect?.contactEmail ?? "",
    source: prospect?.source ?? "",
    stage: prospect?.stage ?? "por_contactar",
    responsableUserId: prospect?.responsableUserId ? String(prospect.responsableUserId) : "",
    nextAction: prospect?.nextAction ?? "",
    lastContactAt: toDateInput(prospect?.lastContactAt ?? null),
    remindAt: toDateInput(prospect?.remindAt ?? null),
    notes: prospect?.notes ?? "",
    archived: prospect?.archived ?? false,
  });

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["seg", "funnel"] });
    qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
    qc.invalidateQueries({ queryKey: ["seg", "alerts"] });
    // updateProspect auto-loguea el cambio de etapa → refrescar el timeline.
    if (prospect) qc.invalidateQueries({ queryKey: ["seg", "activities", prospect.id] });
  };

  const buildBody = () => ({
    type: form.type,
    name: form.name.trim(),
    phone: form.phone || null,
    contactEmail: form.contactEmail || null,
    source: form.source || null,
    stage: form.stage,
    responsableUserId: form.responsableUserId ? Number(form.responsableUserId) : null,
    nextAction: form.nextAction || null,
    // Mediodía UTC para que la fecha date-only no se corra un día al mostrarse
    // en Chile (UTC-3/-4) — clase de bug ya conocida en el proyecto.
    lastContactAt: form.lastContactAt ? `${form.lastContactAt}T12:00:00.000Z` : null,
    remindAt: form.remindAt ? `${form.remindAt}T12:00:00.000Z` : null,
    notes: form.notes || null,
    archived: form.archived,
  });

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? seguimientoApi.createProspect(buildBody())
        : seguimientoApi.updateProspect(prospect!.id, buildBody()),
    onSuccess: () => {
      push({ kind: "success", message: isNew ? "Prospecto creado." : "Prospecto actualizado." });
      invalidate();
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo guardar." }),
  });

  const del = useMutation({
    mutationFn: () => seguimientoApi.deleteProspect(prospect!.id),
    onSuccess: () => {
      push({ kind: "success", message: "Prospecto eliminado." });
      invalidate();
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo eliminar." }),
  });

  const reminder = useMutation({
    mutationFn: () => invitationsApi.resend(prospect!.lastInvitationId as number, true),
    onSuccess: () => {
      push({ kind: "success", message: "Recordatorio enviado." });
      qc.invalidateQueries({ queryKey: ["seg", "funnel"] });
      qc.invalidateQueries({ queryKey: ["seg", "activities", prospect!.id] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo enviar." }),
  });

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Cambiar de etapa auto-rellena la "Próxima acción" con el default de la nueva
  // etapa — pero solo si estaba vacía o traía un default de la etapa anterior
  // (nunca pisa algo escrito a mano / custom).
  const setStage = (newStage: ProspectStage) =>
    setForm((f) => {
      const oldDefault = NEXT_ACTIONS_BY_STAGE[f.stage]?.[0];
      const isCustomAction = !!f.nextAction && !ALL_NEXT_ACTIONS.includes(f.nextAction);
      // Solo auto-rellenar si estaba vacía o tenía EXACTAMENTE el default de la
      // etapa anterior — nunca pisa texto libre NI una sugerencia no-default
      // elegida a mano.
      const shouldAuto = !isCustomAction && (!f.nextAction || f.nextAction === oldDefault);
      const nextAction = shouldAuto
        ? NEXT_ACTIONS_BY_STAGE[newStage]?.[0] ?? f.nextAction
        : f.nextAction;
      return { ...f, stage: newStage, nextAction };
    });

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[480px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <h2 className="nx-h2 truncate">{isNew ? "Nuevo prospecto" : prospect!.name}</h2>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Tipo</FieldLabel>
              <select
                value={form.type}
                onChange={(e) => set("type", e.target.value as ProspectType)}
                className="nx-input"
              >
                {(Object.keys(TYPE_LABELS) as ProspectType[]).map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>Etapa</FieldLabel>
              <select
                value={form.stage}
                onChange={(e) => setStage(e.target.value as ProspectStage)}
                className="nx-input"
              >
                {STAGE_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <FieldLabel>Nombre / Proyecto</FieldLabel>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="nx-input"
              placeholder="Nombre del prospecto"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Email</FieldLabel>
              <input
                value={form.contactEmail}
                onChange={(e) => set("contactEmail", e.target.value)}
                className="nx-input"
                placeholder="correo@…"
              />
            </div>
            <div>
              <FieldLabel>Teléfono</FieldLabel>
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="nx-input"
                placeholder="+56…"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Responsable</FieldLabel>
              <select
                value={form.responsableUserId}
                onChange={(e) => set("responsableUserId", e.target.value)}
                className="nx-input"
              >
                <option value="">— Sin asignar —</option>
                {team.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>Origen del lead</FieldLabel>
              <SelectOrCustom
                value={form.source}
                onChange={(v) => set("source", v)}
                groups={[{ options: LEAD_SOURCES }]}
                placeholder="¿Cómo llegó?"
                emptyLabel="— Sin origen —"
              />
            </div>
          </div>

          <div>
            <FieldLabel>Próxima acción</FieldLabel>
            <SelectOrCustom
              value={form.nextAction}
              onChange={(v) => set("nextAction", v)}
              groups={[
                {
                  label: "Sugeridas para esta etapa",
                  options: NEXT_ACTIONS_BY_STAGE[form.stage] ?? [],
                },
                {
                  label: "Otras acciones",
                  options: ALL_NEXT_ACTIONS.filter(
                    (a) => !(NEXT_ACTIONS_BY_STAGE[form.stage] ?? []).includes(a),
                  ),
                },
              ]}
              placeholder="Qué hay que hacer"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Últ. contacto</FieldLabel>
              <input
                type="date"
                value={form.lastContactAt}
                onChange={(e) => set("lastContactAt", e.target.value)}
                className="nx-input"
              />
            </div>
            <div>
              <FieldLabel>Recordar el día</FieldLabel>
              <input
                type="date"
                value={form.remindAt}
                onChange={(e) => set("remindAt", e.target.value)}
                className="nx-input"
              />
            </div>
          </div>

          <div>
            <FieldLabel>Notas / Estado real</FieldLabel>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="nx-input resize-y"
            />
          </div>

          {/* Invitación activa → reenviar recordatorio (copy según etapa/perfil). */}
          {!isNew && prospect?.lastInvitationStatus === "invited" && (
            <div className="flex items-center justify-between gap-2 border border-border rounded-md px-3 py-2">
              <div className="text-2xs text-fg-muted">
                Invitación{" "}
                {prospect.lastInvitationOpenedAt ? "abierta, sin completar" : "enviada, sin abrir"}
                {prospect.lastInvitationSentAt && ` · ${formatDate(prospect.lastInvitationSentAt)}`}
              </div>
              <button
                className="nx-btn-secondary px-2 py-1 text-2xs whitespace-nowrap"
                disabled={reminder.isPending}
                onClick={() => reminder.mutate()}
                title="Reenvía un recordatorio (el texto se ajusta si ya abrió el link)"
              >
                <Bell size={12} /> {reminder.isPending ? "…" : "Reenviar recordatorio"}
              </button>
            </div>
          )}

          {/* Timeline de actividad (solo prospectos ya creados). */}
          {!isNew && prospect && <ProspectTimeline prospectId={prospect.id} />}

          {prospect?.linkedUser && (
            <div className="text-2xs text-fg-subtle border border-border rounded-md px-3 py-2">
              Enlazado a la cuenta <span className="text-fg">{prospect.linkedUser.email}</span>
              {prospect.linkedUser.lastSeenAt && ` · últ. actividad ${formatDate(prospect.linkedUser.lastSeenAt)}`}
            </div>
          )}

          {!isNew && (
            <label className="flex items-center gap-2 text-xs text-fg-muted cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.archived}
                onChange={(e) => set("archived", e.target.checked)}
              />
              Archivar (ocultar del funnel activo)
            </label>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-border bg-bg-surface flex items-center gap-2 justify-end">
          {!isNew && (
            <button
              onClick={async () => {
                const ok = await confirmAction({
                  title: "Eliminar prospecto",
                  message: `Se borra "${prospect!.name}" del CRM. No se puede deshacer.`,
                  confirmLabel: "Eliminar",
                  tone: "danger",
                });
                if (ok) del.mutate();
              }}
              disabled={del.isPending}
              className="nx-btn-danger mr-auto"
            >
              <Trash2 size={14} /> Eliminar
            </button>
          )}
          <button onClick={onClose} className="nx-btn-secondary">
            Cancelar
          </button>
          <button
            onClick={() => {
              if (!form.name.trim()) {
                push({ kind: "error", message: "El nombre es obligatorio." });
                return;
              }
              save.mutate();
            }}
            disabled={save.isPending}
            className="nx-btn-primary"
          >
            <Check size={14} /> {isNew ? "Crear" : "Guardar"}
          </button>
        </footer>
      </aside>
    </>
  );
}

/**
 * Timeline de actividad de un prospecto: historial de eventos (notas, contactos,
 * cambios de etapa, invitaciones, sistema) + acciones rápidas para sumar una
 * nota o registrar un contacto (esto último mueve la fecha y reagenda el
 * recordatorio en el backend).
 */
function ProspectTimeline({ prospectId }: { prospectId: number }) {
  const qc = useQueryClient();
  const { push } = useToast();
  const [note, setNote] = useState("");

  const q = useQuery({
    queryKey: ["seg", "activities", prospectId],
    queryFn: () => seguimientoApi.prospectActivities(prospectId),
  });

  const invalidateActivities = () =>
    qc.invalidateQueries({ queryKey: ["seg", "activities", prospectId] });

  const addNote = useMutation({
    mutationFn: () => seguimientoApi.addProspectActivity(prospectId, { kind: "nota", body: note.trim() }),
    onSuccess: () => {
      setNote("");
      invalidateActivities();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo agregar la nota." }),
  });

  const logContact = useMutation({
    mutationFn: () =>
      seguimientoApi.addProspectActivity(prospectId, {
        kind: "contacto",
        body: note.trim() || undefined,
      }),
    onSuccess: () => {
      setNote("");
      invalidateActivities();
      // Un contacto mueve lastContactAt + remindAt en el backend → refrescar
      // funnel/métricas/alertas para que se note de inmediato.
      qc.invalidateQueries({ queryKey: ["seg", "funnel"] });
      qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
      qc.invalidateQueries({ queryKey: ["seg", "alerts"] });
      push({ kind: "success", message: "Contacto registrado." });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo registrar." }),
  });

  const items = q.data ?? [];

  return (
    <div>
      <FieldLabel>Actividad</FieldLabel>
      <div className="flex items-center gap-1.5 mb-2">
        <input
          className="nx-input flex-1"
          value={note}
          placeholder="Nota o detalle del contacto…"
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && note.trim()) addNote.mutate();
          }}
        />
        <button
          className="nx-btn-secondary px-2 py-1 text-2xs whitespace-nowrap"
          disabled={addNote.isPending || !note.trim()}
          onClick={() => addNote.mutate()}
          title="Agregar una nota al historial"
        >
          <StickyNote size={12} /> Nota
        </button>
        <button
          className="nx-btn-secondary px-2 py-1 text-2xs whitespace-nowrap"
          disabled={logContact.isPending}
          onClick={() => logContact.mutate()}
          title="Registrar que lo contactaste hoy (mueve la fecha y reagenda el recordatorio)"
        >
          <PhoneCall size={12} /> Contacté hoy
        </button>
      </div>

      {q.isLoading ? (
        <p className="text-2xs text-fg-subtle">Cargando actividad…</p>
      ) : items.length === 0 ? (
        <p className="text-2xs text-fg-subtle">Sin actividad todavía.</p>
      ) : (
        <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
          {items.map((a: ProspectActivity) => (
            <div key={a.id} className="flex items-start gap-2 text-xs">
              <Badge tone={ACTIVITY_KIND_TONE[a.kind] ?? "neutral"}>
                {ACTIVITY_KIND_LABELS[a.kind] ?? a.kind}
              </Badge>
              <div className="flex-1 min-w-0">
                {a.body && <div className="text-fg break-words">{a.body}</div>}
                <div className="text-2xs text-fg-subtle">
                  <History size={10} className="inline mr-0.5 -mt-0.5" />
                  {a.author?.name ? `${a.author.name.split(" ")[0]} · ` : ""}
                  {formatDate(a.createdAt)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
